import React from "react"
import PropTypes from "prop-types"
import logger from "utils/logger"

/**
 * React Error Boundary that catches unhandled render / lifecycle errors in its
 * subtree and renders a fallback UI instead of crashing the whole app.
 *
 * @example
 * <ErrorBoundary>
 *   <SomeComplexComponent />
 * </ErrorBoundary>
 *
 * // Custom fallback:
 * <ErrorBoundary fallback={<p>Something went wrong.</p>}>
 *   <SomeComplexComponent />
 * </ErrorBoundary>
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  componentDidCatch(error, info) {
    logger.error("[ErrorBoundary] Uncaught error", error, info)
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null })
  }

  render() {
    const { hasError, error } = this.state
    const { fallback, children } = this.props

    if (!hasError) return children

    if (fallback) return fallback

    return (
      <div className="d-flex flex-column align-items-center justify-content-center p-4 text-center">
        <i className="mdi mdi-alert-circle-outline display-4 text-danger mb-3"></i>
        <h4>Something went wrong</h4>
        <p className="text-muted mb-3">
          {error?.message || "An unexpected error occurred."}
        </p>
        <button className="btn btn-primary" onClick={this.handleReset}>
          Try Again
        </button>
      </div>
    )
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  fallback: PropTypes.node,
}

ErrorBoundary.defaultProps = {
  fallback: null,
}

export default ErrorBoundary
